import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { X, XIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import type { DraftPayment } from "@/apis/gql/types";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import {
  ToggleGroupInput,
  ToggleGroupInputItem,
} from "@/components/shared/form/toggle-group-input";
import { Button } from "@/components/ui/button";
import { Form as FormProvider } from "@/components/ui/form";
import {
  INVOICE_QUERY,
  type InvoiceDetailsDraftPayment,
} from "@/queries/invoices/get";
import { useCreateDraftPayment } from "@/queries/payments/draft-payments/create-draft-payment";
import { useUpdateDraftPayment } from "@/queries/payments/draft-payments/update-draft-payment";

type Props = {
  invoiceId: string;
  draftPayments: InvoiceDetailsDraftPayment[];
  totalAmount: number;
  onValuesChange?: (values: Partial<DiscountFormValues>) => void;
  onFieldFocus: (field: string) => void;
  onClose?: () => void;
};

export const EditDiscountForm = ({
  invoiceId,
  draftPayments,
  totalAmount,
  onValuesChange,
  onFieldFocus,
  onClose,
}: Props) => {
  const [createDraftPayment] = useCreateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const { updateDraftPayment } = useUpdateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const handleSubmit = (values: DiscountFormValues) => {
    // TODO: Implement saving logic
    console.log("Discount values:", values);
    updateDraftPayment({
      draftPaymentId: draftPayments[0].draftPaymentId,
      discount: Number(values.discountPercentage),
    });
  };

  if (draftPayments?.length === 0) {
    // return (
    //   <Form
    //     values={{}}
    //     draftPayment={{}}
    //     totalAmount={totalAmount}
    //     onSubmit={handleSubmit}
    //     onValuesChange={onValuesChange}
    //     onFieldFocus={onFieldFocus}
    //     onClose={onClose}
    //   />
    // );
  }

  return (
    <div className="flex flex-col gap-6">
      {draftPayments?.map((payment, index) => (
        <Form
          key={payment.draftPaymentId}
          index={index + 1}
          values={{
            discountPercentage: payment.discount?.toString() || "0",
          }}
          draftPayment={payment}
          totalAmount={totalAmount}
          onSubmit={handleSubmit}
          onValuesChange={onValuesChange}
          onFieldFocus={onFieldFocus}
        />
      ))}
    </div>
  );
};

const discountFormSchema = z.object({
  discountPercentage: z
    .union([z.number(), z.string().transform((val) => Number(val))])
    .pipe(z.number().min(0).max(100))
    .optional()
    .nullable(),
});

type DiscountFormValues = {
  discountPercentage?: string | number | null;
};

type FormProps = {
  values: DiscountFormValues;
  index: number;
  draftPayment: InvoiceDetailsDraftPayment;
  totalAmount: number;
  disabled?: boolean;
  onSubmit: (values: DiscountFormValues) => void;
  onValuesChange?: (values: DiscountFormValues) => void;
  onFieldFocus: (field: string) => void;
  onClose?: () => void;
};

const Form = ({
  values,
  index,
  draftPayment,
  totalAmount,
  disabled = false,
  onSubmit,
  onValuesChange,
  onFieldFocus,
  onClose,
}: FormProps) => {
  const { t } = useTranslation();

  const form = useForm<DiscountFormValues>({
    resolver: zodResolver(discountFormSchema),
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const formValues = form.watch();
  const isDirty = form.formState.isDirty;

  const handleSubmit = (values: DiscountFormValues) => {
    onSubmit(values);
  };

  const amount = draftPayment.amount ?? 0;
  const rawAmount = draftPayment.rawAmount ?? 0;

  const discountPercentage = Number(formValues.discountPercentage) || 0;

  useEffect(() => {
    form.trigger();
  }, []);

  useDeepCompareEffect(() => {
    if (isDirty) {
      onValuesChange?.(formValues);
    }
  }, [formValues, onValuesChange, isDirty]);

  useEffect(() => {
    if (formValues.discountPercentage === "0") {
      form.setValue("discountPercentage", "0.0", { shouldDirty: true }); // small hack to de-focus the X-toggle
    }
  }, [formValues.discountPercentage, form, onFieldFocus]);

  return (
    <FormProvider {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex items-center ">
          <h3 className="text-sm font-medium text-gray-900">
            {t("discount.title", "Skonto")}
          </h3>
        </div>
        <div className="flex items-start gap-1.5">
          <div className="flex-shrink-0 w-30">
            <FormNumberInput
              name="discountPercentage"
              suffix="%"
              disableFastCentInput
            />
          </div>

          <ToggleGroupInput
            name="discountPercentage"
            variant="outline"
            className="!justify-start"
          >
            <ToggleGroupInputItem className="h-10" value="3">
              3%
            </ToggleGroupInputItem>
            <ToggleGroupInputItem className="h-10" value="2">
              2%
            </ToggleGroupInputItem>
            <ToggleGroupInputItem className="h-10" value="1">
              1%
            </ToggleGroupInputItem>
            <ToggleGroupInputItem value="0" className="text-gray-400 h-10">
              <XIcon className="h-5 w-5" />
            </ToggleGroupInputItem>
          </ToggleGroupInput>
        </div>

        {discountPercentage > 0 && amount !== rawAmount && (
          <div className="text-sm text-gray-600">
            <p>
              {t("discount.amount_with_discount", "Zahlungsbetrag mit Skonto")}:{" "}
              <span className="font-medium">
                {new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: "EUR",
                }).format(amount)}
              </span>
            </p>
          </div>
        )}

        <AutoSaveForm onSubmit={handleSubmit} immediate />
      </form>
    </FormProvider>
  );
};

export type EditDiscountFormValues = DiscountFormValues;
